import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { HierarchyReportData, HierarchyNode, HierarchyColumn } from '../../interfaces/hierarchy.interface';
import { HierarchyService } from '../../services/hierarchy.service';

interface DisplayRow {
  _hierarchyId: number;
  _hierarchyName: string;
  _level: number;
  _isParentRow: boolean; // Is this the parent's own data row (arrow icon)?
  _hasChildren: boolean; // Does this row show expand/collapse icon?
  _isExpanded: boolean;
  _node: HierarchyNode;
  _isExpandRow?: boolean; // Is this row the main row with expand control?
  _childUserIds?: number[]; // Child user IDs from hierarchyObject
  _parentIdAsChild?: boolean; // Parent ID as child ID
  [key: string]: any; // Column data
}

@Component({
  selector: 'bi-hierarchy-report',
  templateUrl: './hierarchy-report.component.html',
  styleUrls: ['./hierarchy-report.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    TooltipModule
  ],
  providers: [HierarchyService]
})
export class HierarchyReportComponent implements OnInit, OnChanges {
  @Input() hierarchyData: HierarchyReportData | null = null;
  @Input() columns: HierarchyColumn[] = [];
  @Input() showSums: boolean = false;
  @Input() expandAll: boolean = false;
  @Input() maxDepth: number = 10;
  @Output() getDrilldownReportInfo: EventEmitter<any> = new EventEmitter();

  expandedNodes: Set<number> = new Set();
  displayRows: DisplayRow[] = [];

  constructor(private hierarchyService: HierarchyService) {}

  ngOnInit(): void {
    this.processData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['hierarchyData'] || changes['columns']) {
      this.processData();
    }
    if (changes['expandAll']) {
      this.handleExpandAllChange();
    }
  }

  private processData(): void {
    if (!this.hierarchyData) {
      this.displayRows = [];
      return;
    }

    // Create columns if not provided
    let columnsToUse = this.columns;
    if (columnsToUse.length === 0 && this.hierarchyData.nodes.length > 0) {
      columnsToUse = this.createDefaultColumns();
    }
    this.columns = columnsToUse;

    // Build display rows
    this.buildDisplayRows();
  }

  private createDefaultColumns(): HierarchyColumn[] {
    const defaultColumns: HierarchyColumn[] = [];
    
    if (this.hierarchyData && this.hierarchyData.nodes.length > 0) {
      const firstNode = this.hierarchyData.nodes[0];
      if (firstNode.data) {
        Object.keys(firstNode.data).forEach(key => {
          if (key !== 'hierarchyObject') {
            // Skip username/name columns since hierarchy column already displays names
            const keyLower = key.toLowerCase();
            if (keyLower.includes('username') || 
                keyLower.includes('name') || 
                keyLower.includes('user') ||
                keyLower.includes('displayname')) {
              return; // Skip this column
            }
            
            const value = firstNode.data[key];
            const isNumeric = typeof value === 'number' || !isNaN(Number(value));
            defaultColumns.push({
              columnId: key,
              columnName: key.replace(/_/g, ' ').replace(/col__/g, '').replace(/org_/g, ''),
              dataType: isNumeric ? '1' : '0',
              isNumeric: isNumeric,
              isSummable: isNumeric
            });
          }
        });
      }
    }
    
    return defaultColumns;
  }

  private buildDisplayRows(): void {
    this.displayRows = [];
    if (!this.hierarchyData) return;

    this.hierarchyData.rootNodes.forEach(rootNode => {
      this.addNodeToDisplay(rootNode, 0);
    });
  }

  private addNodeToDisplay(node: HierarchyNode, level: number): void {
    //console.log('node 120', node);
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = this.expandedNodes.has(node.id);
    
    // Extract childUserIds from hierarchyObject
    const childUserIds = node.data?.hierarchyObject?.childUserIds || [];
    const parentIdAsChild = node.data?.hierarchyObject?.parentIdAsChild || false;

    // 1. Main row - shows aggregated values with +/- icon if has children
    const mainRow: DisplayRow = {
      _hierarchyId: node.id,
      _hierarchyName: node.displayName,
      _level: level,
      _isParentRow: false,
      _hasChildren: hasChildren,  // Show +/- icon if has children
      _isExpanded: isExpanded,
      _node: node,
      _isExpandRow: hasChildren,  // This is the expand control if has children
      _childUserIds: childUserIds,  // Add childUserIds as direct key
      _parentIdAsChild: parentIdAsChild,  // Add parentIdAsChild as direct key
      ...this.getRowData(node)
    };
    this.displayRows.push(mainRow);

    // 2. If expanded and has children, show parent's own data row and children
    if (isExpanded && hasChildren) {
      // Parent's own data row (→ icon) - shows only parent's individual contribution
      const parentDataRow: DisplayRow = {
        _hierarchyId: node.id,
        _hierarchyName: node.displayName,
        _level: level + 1,
        _isParentRow: true,  // Shows arrow icon
        _hasChildren: false,
        _isExpanded: false,
        _node: node,
        _childUserIds: childUserIds,  // Add childUserIds as direct key
        _parentIdAsChild: parentIdAsChild,  // Add parentIdAsChild as direct key
        ...this.getParentOwnData(node)
      };
      this.displayRows.push(parentDataRow);

      // Children
      node.children.forEach(child => {
        this.addNodeToDisplay(child, level + 1);
      });
    }
  }

  private getRowData(node: HierarchyNode): any {
    const rowData: any = {};
    
    // Add all data from the node
    if (node.data) {
      Object.keys(node.data).forEach(key => {
        if (key !== 'hierarchyObject') {
          rowData[key] = node.data[key];
        }
      });
    }

    // Add by column index
    this.columns.forEach((column, index) => {
      if (node.data[column.columnId] !== undefined) {
        rowData[index] = node.data[column.columnId];
      } else if (node.data[index] !== undefined) {
        rowData[index] = node.data[index];
      }
    });

    return rowData;
  }

  private getParentOwnData(node: HierarchyNode): any {
    console.log('Getting parent own data for:', node.displayName);
    
    // If backend provided individual data separately, use it
    if (node.individualData) {
      console.log('✅ Using individual data from backend:', node.individualData);
      const rowData: any = {};
      
      // Add all data from individual data
      if (node.individualData) {
        Object.keys(node.individualData).forEach(key => {
          if (key !== 'hierarchyObject') {
            rowData[key] = node.individualData[key];
          }
        });
      }
      
      // Add by column index
      this.columns.forEach((column, index) => {
        if (node.individualData[column.columnId] !== undefined) {
          rowData[index] = node.individualData[column.columnId];
        } else if (node.individualData[index] !== undefined) {
          rowData[index] = node.individualData[index];
        }
      });
      
      return rowData;
    }
    
    // Otherwise, calculate by subtracting children's contributions
    console.log('📊 Calculating parent own data by subtracting children');
    const rowData: any = {};
    
    // Calculate children's sum for each column
    const childrenSumByIndex: { [key: number]: number } = {};
    
    // Sum up all children's values
    node.children.forEach(child => {
      this.columns.forEach((column, index) => {
        const childValue = child.data[column.columnId] || child.data[index];
        if (typeof childValue === 'number') {
          childrenSumByIndex[index] = (childrenSumByIndex[index] || 0) + childValue;
        }
      });
    });
    
    // Add data by column index
    this.columns.forEach((column, index) => {
      let totalValue: any;
      
      // Try to get total value
      if (node.data[column.columnId] !== undefined) {
        totalValue = node.data[column.columnId];
      } else if (node.data[index] !== undefined) {
        totalValue = node.data[index];
      }
      
      if (totalValue !== undefined) {
        if (column.isNumeric && typeof totalValue === 'number' && node.children.length > 0) {
          const childrenSum = childrenSumByIndex[index] || 0;
          const ownValue = totalValue - childrenSum;
          rowData[index] = ownValue;
          if (column.columnId) {
            rowData[column.columnId] = ownValue;
          }
          console.log(`Column ${index} (${column.columnName}): Total=${totalValue}, Children=${childrenSum}, Own=${ownValue}`);
        } else {
          rowData[index] = totalValue;
          if (column.columnId) {
            rowData[column.columnId] = totalValue;
          }
        }
      }
    });
    
    // Also copy non-column data
    if (node.data) {
      Object.keys(node.data).forEach(key => {
        if (key !== 'hierarchyObject' && rowData[key] === undefined) {
          rowData[key] = node.data[key];
        }
      });
    }
    
    return rowData;
  }

  private handleExpandAllChange(): void {
    if (this.expandAll) {
      this.expandAllNodes();
    } else {
      this.collapseAllNodes();
    }
  }

  private expandAllNodes(): void {
    if (this.hierarchyData) {
      this.hierarchyData.nodes.forEach(node => {
        this.expandedNodes.add(node.id);
      });
    }
    this.buildDisplayRows();
  }

  private collapseAllNodes(): void {
    this.expandedNodes.clear();
    this.buildDisplayRows();
  }

  toggleNode(nodeId: number): void {
    if (this.expandedNodes.has(nodeId)) {
      this.expandedNodes.delete(nodeId);
    } else {
      this.expandedNodes.add(nodeId);
    }
    this.buildDisplayRows();
  }

  getIndent(level: number, row?: DisplayRow): string {
    // Expand control rows get extra indentation
    const baseIndent = level * 24;
    return `${baseIndent}px`;
  }

  getExpandIcon(row: DisplayRow): string {
    if (!row._hasChildren) {
      return '';
    }
    return row._isExpanded ? 'pi pi-minus' : 'pi pi-plus';
  }

  shouldShowExpandIcon(row: DisplayRow): boolean {
    // Show +/- icon if row has children and is not a parent data row
    return row._hasChildren && !row._isParentRow;
  }

  shouldShowArrowIcon(row: DisplayRow): boolean {
    // Only parent data rows show the arrow icon
    return row._isParentRow === true;
  }

  shouldShowSpacer(row: DisplayRow): boolean {
    // Show spacer for rows without expand icon or arrow (leaf nodes)
    return !row._hasChildren && !row._isParentRow;
  }

  getColumnValue(row: DisplayRow, column: HierarchyColumn, index: number): any {
    // Try by column ID first
    if (row[column.columnId] !== undefined) {
      return row[column.columnId];
    }
    
    // Try by index
    if (row[index] !== undefined) {
      return row[index];
    }
    
    return '';
  }

  formatValue(value: any, column: HierarchyColumn): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    
    if (column.isNumeric) {
      const num = typeof value === 'number' ? value : parseFloat(value);
      if (!isNaN(num)) {
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format(num);
      }
    }
    
    return String(value);
  }

  getColumnClass(column: HierarchyColumn): string {
    return column.isNumeric ? 'text-right' : 'text-left';
  }

  getRowClass(row: DisplayRow): string {
    const classes = ['hierarchy-row'];
    
    if (row._isParentRow) {
      classes.push('parent-data-row');
    } else if (row._hasChildren) {
      classes.push('parent-row');
    } else {
      classes.push('leaf-row');
    }
    
    classes.push(`level-${row._level}`);
    return classes.join(' ');
  }

  getDrilldownReport(row: DisplayRow, column: any, index: number): void {
    console.log('row', row);
    let drilldownObject: any = {};
    drilldownObject.filterColumn = column.columnId;
    if (row._isParentRow) {
      drilldownObject.filterValue = row._hierarchyId;
    } else {
      let tempFilterValue = row._childUserIds && row._childUserIds.length > 0 ? row._childUserIds : [];
      tempFilterValue.push(row._hierarchyId);
      drilldownObject.filterValue = tempFilterValue;
    }
    //this.getDrilldownReport(drilldownObject, column, 1);
    this.getDrilldownReportInfo.emit(drilldownObject);
  }
}
